import { Link, createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { DiscordIcon } from '#bv/components/DiscordIcon'
import { getManualFfOrder, markManualFfPaymentSent } from '#bv/lib/manualFf'
import type { ManualFfOrder, ManualFfOrderStatus } from '#bv/lib/adminTypes'
import { buyerExportDisputePack } from '#bv/lib/buyerDisputeServerFn'
import { manualFfOrderEligibleForDisputePack } from '#bv/lib/manualFfDisputeEligibility'
import { clearMmCheckoutContext, readMmCheckoutContext, type MmCheckoutStash } from '#bv/lib/mmCheckoutSession'
import { getCryptoLedgerSummary } from '#bv/lib/nowpayments'
import { BuyerSmmFulfillment } from '#bv/components/customer/BuyerSmmFulfillment'
import {
  hasBuyerSmmOrder,
  smmCheckoutSuccessMessage,
  type BuyerSmmStatusFields,
} from '#bv/lib/buyerSmmStatus'
import { orderItemsToPasteSummary, supportPasteProductSection } from '#bv/lib/supportTicketPaste'
import { PulseDigital_VOUCHES_CHANNEL, PulseDigital_VOUCHES_CHANNEL_URL } from '#bv/lib/replacementPolicy'

export const Route = createFileRoute('/checkout/success')({
  validateSearch: z.object({
    manualOrderId: z.string().optional(),
    /** Set to `1` when redirecting from crypto checkout after payment. */
    crypto: z.string().optional(),
    /** NOWPayments `paymentId` from `/checkout/crypto/$paymentId` — used to load line items for the support template. */
    paymentId: z.string().optional(),
  }),
  component: CheckoutSuccess,
})

function CheckoutSuccess() {
  const search = Route.useSearch()
  const manualOrderId = search.manualOrderId?.trim()
  const fromCrypto = search.crypto === '1'
  const cryptoPaymentId = search.paymentId?.trim() ?? ''
  const [manualOrder, setManualOrder] = useState<ManualFfOrder | null>(null)
  const [loadingManualOrder, setLoadingManualOrder] = useState(false)
  const [markingSent, setMarkingSent] = useState(false)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState<
    'ticket' | 'order' | 'note' | 'mmSession' | 'mmPaste' | 'cryptoPaste' | 'cryptoPayId' | ''
  >('')
  const [cryptoLedger, setCryptoLedger] = useState<{
    items: { productId: number; name: string; unitPrice: number; quantity: number }[]
    buyerEmail: string
    totalUsd: number
    internalOrderId: string
  } | null>(null)
  const [cryptoLedgerLoading, setCryptoLedgerLoading] = useState(false)
  const [mmCardCtx, setMmCardCtx] = useState<MmCheckoutStash | null>(null)
  const [mmDisputeEmail, setMmDisputeEmail] = useState('')
  const [mmDisputeBusy, setMmDisputeBusy] = useState(false)
  const [mmDisputeErr, setMmDisputeErr] = useState('')
  /** Polled via /api/buyer-order-status — card (webhook), crypto (ledger), or PayPal F&F (manual store). */
  const [buyerServerPoll, setBuyerServerPoll] = useState<{
    kind: 'moneymotion' | 'crypto' | 'paypal_ff' | null
    ready: boolean | null
    err: string
    smm: BuyerSmmStatusFields | null
  }>({ kind: null, ready: null, err: '', smm: null })
  const [ffDisputeEmail, setFfDisputeEmail] = useState('')
  const [ffDisputeBusy, setFfDisputeBusy] = useState(false)
  const [ffDisputeErr, setFfDisputeErr] = useState('')

  const loadManualOrder = useCallback(async () => {
    if (!manualOrderId) return
    setLoadingManualOrder(true)
    setError('')
    try {
      const order = await getManualFfOrder({ data: { orderId: manualOrderId } })
      setManualOrder(order as ManualFfOrder)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load order details.')
    } finally {
      setLoadingManualOrder(false)
    }
  }, [manualOrderId])

  useEffect(() => {
    void loadManualOrder()
  }, [loadManualOrder])

  useEffect(() => {
    if (!fromCrypto || !cryptoPaymentId) {
      setCryptoLedger(null)
      setCryptoLedgerLoading(false)
      return
    }
    setCryptoLedgerLoading(true)
    void getCryptoLedgerSummary({ data: { paymentId: cryptoPaymentId } })
      .then((row) => setCryptoLedger(row))
      .catch(() => setCryptoLedger(null))
      .finally(() => setCryptoLedgerLoading(false))
  }, [fromCrypto, cryptoPaymentId])

  useEffect(() => {
    if (manualOrderId) {
      clearMmCheckoutContext()
      return
    }
    if (fromCrypto) {
      clearMmCheckoutContext()
      return
    }
    setMmCardCtx(readMmCheckoutContext())
  }, [manualOrderId, fromCrypto])

  useEffect(() => {
    if (mmCardCtx?.buyerEmail) setMmDisputeEmail(mmCardCtx.buyerEmail)
  }, [mmCardCtx?.buyerEmail])

  useEffect(() => {
    let kind: 'moneymotion' | 'crypto' | 'paypal_ff' | null = null
    let orderId = ''
    let emailQ = ''

    if (mmCardCtx?.checkoutSessionId?.trim()) {
      kind = 'moneymotion'
      orderId = mmCardCtx.checkoutSessionId.trim()
      emailQ = (mmCardCtx.buyerEmail?.trim() || mmDisputeEmail.trim()) || ''
    } else if (fromCrypto && cryptoPaymentId.trim()) {
      kind = 'crypto'
      orderId = cryptoPaymentId.trim()
      emailQ = cryptoLedger?.buyerEmail?.trim() || ''
    } else if (manualOrderId?.trim() && manualOrder) {
      kind = 'paypal_ff'
      orderId = manualOrderId.trim()
      emailQ = (manualOrder.buyerEmail?.trim() || ffDisputeEmail.trim()) || ''
    }

    if (!kind || !orderId) {
      setBuyerServerPoll({ kind: null, ready: null, err: '', smm: null })
      return
    }

    const poll = async () => {
      const qs = new URLSearchParams({ orderId })
      if (emailQ) qs.set('email', emailQ)
      try {
        const res = await fetch(`/api/buyer-order-status?${qs.toString()}`)
        if (res.status === 401) {
          const j = (await res.json()) as { error?: string }
          setBuyerServerPoll({
            kind,
            ready: null,
            err:
              j.error === 'email_required'
                ? kind === 'moneymotion'
                  ? 'Enter your checkout email below so we can verify this session.'
                  : kind === 'crypto'
                    ? 'We need your checkout email to verify this invoice — it should appear above when your order summary loads.'
                    : 'Enter the buyer email for this order (above) so we can verify it matches.'
                : 'Email does not match this order. Use the same address you used at checkout.',
            smm: null,
          })
          return
        }
        if (!res.ok) {
          setBuyerServerPoll({ kind, ready: null, err: '', smm: null })
          return
        }
        const data = (await res.json()) as {
          ok?: boolean
          readyForDelivery?: boolean
          status?: string
          channel?: string
          smmStatus?: string
          smmOrderId?: string
          smmTiktokTarget?: string
          smmPackageLabel?: string
          smmQuantity?: number
          smmPlatform?: BuyerSmmStatusFields['smmPlatform']
          smmMetric?: BuyerSmmStatusFields['smmMetric']
        }
        const ch =
          data.channel === 'crypto' || data.channel === 'paypal_ff' || data.channel === 'moneymotion'
            ? data.channel
            : kind
        const paid = data.ok && (data.readyForDelivery === true || data.status === 'paid')
        const smmFields: BuyerSmmStatusFields = {
          smmStatus: data.smmStatus,
          smmOrderId: data.smmOrderId,
          smmTiktokTarget: data.smmTiktokTarget,
          smmPackageLabel: data.smmPackageLabel,
          smmQuantity: data.smmQuantity,
          smmPlatform: data.smmPlatform,
          smmMetric: data.smmMetric,
        }
        setBuyerServerPoll({
          kind: ch,
          ready: paid ? true : data.ok ? false : null,
          err: '',
          smm: hasBuyerSmmOrder(smmFields) ? smmFields : null,
        })
      } catch {
        setBuyerServerPoll({ kind, ready: null, err: '', smm: null })
      }
    }

    void poll()
    const id = window.setInterval(() => void poll(), 4000)
    return () => window.clearInterval(id)
  }, [
    mmCardCtx?.checkoutSessionId,
    mmCardCtx?.buyerEmail,
    mmDisputeEmail,
    fromCrypto,
    cryptoPaymentId,
    cryptoLedger?.buyerEmail,
    manualOrderId,
    manualOrder,
    ffDisputeEmail,
  ])

  useEffect(() => {
    if (manualOrder?.buyerEmail) setFfDisputeEmail(manualOrder.buyerEmail)
  }, [manualOrder?.buyerEmail])

  const downloadBuyerDisputePdf = useCallback(
    async (
      body:
        | { kind: 'moneymotion'; checkoutSessionId: string; email: string }
        | { kind: 'manual_ff'; orderId: string; email: string },
    ) => {
      const out = await buyerExportDisputePack({ data: body })
      const bin = atob(out.pdfBase64)
      const bytes = new Uint8Array(bin.length)
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
      const blob = new Blob([bytes], { type: 'application/pdf' })
      const m = out.contentDisposition.match(/filename="([^"]+)"/)
      const filename = m?.[1] ?? 'PulseDigital-dispute-pack.pdf'
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    },
    [],
  )

  const mmDiscordPaste = useMemo(() => {
    if (!mmCardCtx) return ''
    const emailLine = mmCardCtx.buyerEmail
      ? `Checkout email: ${mmCardCtx.buyerEmail}`
      : 'Checkout email: (the address you entered on our cart)'
    const productBlock = supportPasteProductSection(mmCardCtx.purchaseSummary ?? '')
    return `MoneyMotion checkout session ID: ${mmCardCtx.checkoutSessionId}
${emailLine}${productBlock}

Hi support — my card payment completed. Please fulfill this order.`
  }, [mmCardCtx])

  const manualFfTicketPaste = useMemo(() => {
    if (!manualOrder || manualOrder.status !== 'paid') return ''
    const productBlock = supportPasteProductSection(orderItemsToPasteSummary(manualOrder.items))
    return `Order ID: ${manualOrder.id}
Order Note ID: ${manualOrder.noteId}
Buyer Email: ${manualOrder.buyerEmail}
Amount: $${manualOrder.totalUsd.toFixed(2)} USD${productBlock}

Hi support, payment is confirmed. Please deliver my order.`
  }, [manualOrder])

  const cryptoDiscordPaste = useMemo(() => {
    if (!fromCrypto || !cryptoPaymentId) return ''
    const email = cryptoLedger?.buyerEmail?.trim()
    const emailLine = email ? `Checkout email: ${email}` : 'Checkout email: (the address you used on the cart)'
    const productBlock = cryptoLedger?.items?.length
      ? supportPasteProductSection(orderItemsToPasteSummary(cryptoLedger.items))
      : ''
    return `Crypto invoice / payment ID: ${cryptoPaymentId}
${emailLine}${productBlock}

Hi support — my cryptocurrency payment completed. Please fulfill this order.`
  }, [fromCrypto, cryptoPaymentId, cryptoLedger])

  const steps = useMemo(
    () => [
      { id: 'awaiting_payment', label: 'Awaiting Payment', hint: 'Send PayPal F&F using the instructions provided.' },
      { id: 'verifying_payment', label: 'Verifying Payment', hint: 'We are matching your payment and order note ID.' },
      { id: 'paid', label: 'Payment Verified', hint: 'Payment confirmed. Preparing your delivery.' },
      { id: 'delivered', label: 'Order Complete', hint: 'Order/receipt delivered to your email.' },
    ] as Array<{ id: ManualFfOrderStatus; label: string; hint: string }>,
    [],
  )

  const activeStepIndex = useMemo(() => {
    const status = manualOrder?.status ?? 'awaiting_payment'
    const idx = steps.findIndex((s) => s.id === status)
    return idx === -1 ? 0 : idx
  }, [manualOrder?.status, steps])

  const markPaymentSent = async () => {
    if (!manualOrderId) return
    setMarkingSent(true)
    setError('')
    try {
      const updated = await markManualFfPaymentSent({ data: { orderId: manualOrderId } })
      setManualOrder(updated as ManualFfOrder)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update order status.')
    } finally {
      setMarkingSent(false)
    }
  }

  const copyText = async (
    value: string,
    kind: 'ticket' | 'order' | 'note' | 'mmSession' | 'mmPaste' | 'cryptoPaste' | 'cryptoPayId',
  ) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(kind)
      window.setTimeout(() => setCopied(''), 1800)
    } catch {
      setError('Could not copy text. Please copy manually.')
    }
  }

  if (manualOrderId) {
    return (
      <div className="min-h-screen px-4 py-8 sm:px-6 sm:py-12">
        <div className="mx-auto grid w-full max-w-6xl gap-6 lg:grid-cols-[1.2fr_0.95fr]">
          <section
            className="rounded-2xl p-5 sm:p-7"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(0,191,255,0.08)' }}
          >
            <h1 className="font-display text-3xl tracking-wide chrome-text sm:text-4xl">Complete Payment</h1>
            <p className="mt-2 text-sm" style={{ color: '#8ecfff' }}>
              Follow the instructions below to complete your manual PayPal Friends & Family payment.
            </p>

            {loadingManualOrder ? (
              <p className="mt-6 text-sm" style={{ color: '#5a6d8a' }}>
                Loading order details…
              </p>
            ) : manualOrder ? (
              <>
                <div
                  className="mt-6 rounded-xl px-4 py-3 text-sm"
                  style={{ background: 'rgba(64,129,255,0.09)', border: '1px solid rgba(64,129,255,0.2)' }}
                >
                  <p className="mb-2 font-semibold text-[#d8e5ff]">Important</p>
                  <ul className="space-y-1.5 pl-5 text-[#d8e5ff]">
                    <li>Send exactly ${manualOrder.totalUsd.toFixed(2)} via PayPal Friends &amp; Family.</li>
                    <li>Use the order note ID exactly as shown below in your payment note.</li>
                    <li>Click &quot;I Have Sent The Payment&quot; after sending.</li>
                  </ul>
                </div>

                <div className="mt-6 space-y-4">
                  <div>
                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-[#5a6d8a]">PayPal email</p>
                    <div className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 font-mono text-sm text-[#f0f6ff]">
                      {manualOrder.paypalEmail}
                    </div>
                  </div>
                  <div>
                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-[#5a6d8a]">Order note (required)</p>
                    <div className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 font-mono text-sm text-[#aef2ce]">
                      {manualOrder.noteId}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => void markPaymentSent()}
                  disabled={markingSent || manualOrder.status !== 'awaiting_payment'}
                  className="btn-primary mt-6 w-full rounded-xl px-6 py-3 text-sm font-semibold disabled:opacity-60"
                >
                  {manualOrder.status === 'awaiting_payment'
                    ? markingSent
                      ? 'Updating…'
                      : 'I Have Sent The Payment'
                    : 'Payment Submitted'}
                </button>
              </>
            ) : null}
            {error ? (
              <p className="mt-4 text-xs text-red-300">{error}</p>
            ) : null}
          </section>

          <section
            className="rounded-2xl p-5 sm:p-7"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(0,191,255,0.08)' }}
          >
            <h2 className="text-2xl font-semibold">Order Progress</h2>
            <p className="mt-1 text-sm" style={{ color: '#5a6d8a' }}>
              Track your order status in real-time.
            </p>
            {buyerServerPoll.kind === 'paypal_ff' && buyerServerPoll.err ? (
              <p className="mt-3 text-xs text-amber-200">{buyerServerPoll.err}</p>
            ) : null}
            {buyerServerPoll.kind === 'paypal_ff' && buyerServerPoll.ready === true ? (
              <p
                className="mt-3 rounded-lg border border-emerald-500/35 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-100"
                role="status"
              >
                Our server shows this PayPal F&amp;F order as paid. You can open Discord whenever you are ready — staff
                see the same status in admin.
              </p>
            ) : buyerServerPoll.kind === 'paypal_ff' && buyerServerPoll.ready === false ? (
              <p className="mt-3 text-xs text-[#a8b3e8]" role="status">
                Waiting for payment confirmation (after you send F&amp;F, staff marks the order paid). This page checks
                automatically.
              </p>
            ) : null}
            <div className="mt-5 space-y-3">
              {steps.map((step, idx) => {
                const done = idx < activeStepIndex
                const active = idx === activeStepIndex
                return (
                  <div key={step.id} className="flex items-start gap-3">
                    <div
                      className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                      style={{
                        background: done || active ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.06)',
                        color: done || active ? '#86efac' : '#5a6d8a',
                        border: `1px solid ${done || active ? 'rgba(34,197,94,0.45)' : 'rgba(255,255,255,0.08)'}`,
                      }}
                    >
                      {done ? 'âœ“' : idx + 1}
                    </div>
                    <div>
                      <div className="text-sm font-semibold" style={{ color: done || active ? '#f0f6ff' : '#5a6d8a' }}>
                        {step.label}
                      </div>
                      <div className="text-xs" style={{ color: '#5a6d8a' }}>
                        {step.hint}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            {manualOrder ? (
              <div className="mt-6 rounded-xl border border-white/10 bg-black/20 p-4 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span style={{ color: '#5a6d8a' }}>Order ID</span>
                  <span className="font-mono text-xs text-[#8ecfff]">{manualOrder.id}</span>
                </div>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <span style={{ color: '#5a6d8a' }}>Total</span>
                  <span className="font-semibold text-[#f0f6ff]">${manualOrder.totalUsd.toFixed(2)}</span>
                </div>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <span style={{ color: '#5a6d8a' }}>Email</span>
                  <span className="text-xs text-[#8ecfff]">{manualOrder.buyerEmail}</span>
                </div>
                {manualOrder.items?.length ? (
                  <div className="mt-4 border-t border-white/10 pt-3">
                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.15em]" style={{ color: '#5a6d8a' }}>
                      Items
                    </p>
                    <ul className="m-0 list-disc space-y-0.5 pl-4 text-xs text-[#8ecfff]">
                      {orderItemsToPasteSummary(manualOrder.items)
                        .split('\n')
                        .map((line, idx) => (
                          <li key={idx} className="break-words">
                            {line}
                          </li>
                        ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : null}
            {manualOrder?.status === 'paid' ? (
              <div
                className="mt-4 rounded-xl px-4 py-3 text-sm"
                style={{ background: 'rgba(88,101,242,0.12)', border: '1px solid rgba(88,101,242,0.28)' }}
              >
                <p className="font-semibold text-[#dbe2ff]">Payment confirmed</p>
                <p className="mt-1 text-[#c9d4ff]">
                  Open a support ticket in Discord now to receive your product delivery.
                </p>
                <div className="mt-3 rounded-lg border border-white/10 bg-black/20 p-3">
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#a8b3e8]">Ticket message template</p>
                  <pre className="overflow-auto whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-[#e8ecff]">
                    {manualFfTicketPaste}
                  </pre>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => void copyText(manualFfTicketPaste, 'ticket')}
                      className="rounded-md border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#dbe2ff] hover:bg-white/10"
                    >
                      {copied === 'ticket' ? 'Copied template' : 'Copy template'}
                    </button>
                    <button
                      type="button"
                      onClick={() => void copyText(manualOrder.id, 'order')}
                      className="rounded-md border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#dbe2ff] hover:bg-white/10"
                    >
                      {copied === 'order' ? 'Copied order ID' : 'Copy order ID'}
                    </button>
                    <button
                      type="button"
                      onClick={() => void copyText(manualOrder.noteId, 'note')}
                      className="rounded-md border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#dbe2ff] hover:bg-white/10"
                    >
                      {copied === 'note' ? 'Copied note ID' : 'Copy note ID'}
                    </button>
                  </div>
                </div>
                <a
                  href="https://discord.gg/tGxhDfRkdE"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-discord mt-3 rounded-lg px-4 py-2 text-sm"
                >
                  <DiscordIcon size={16} white className="shrink-0" />
                  Open Discord Ticket
                </a>
              </div>
            ) : null}
            {manualOrder && manualFfOrderEligibleForDisputePack(manualOrder) ? (
              <div
                className="mt-4 rounded-xl px-4 py-3 text-sm"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <p className="font-semibold text-[#f0f6ff]">Dispute pack (PDF)</p>
                <p className="mt-1 text-xs text-[#5a6d8a]">
                  Summary for PayPal or your bank if you need to open a case. Must match the buyer email on this order.
                </p>
                <label className="mt-3 block">
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.12em] text-[#5a6d8a]">Buyer email</span>
                  <input
                    type="email"
                    value={ffDisputeEmail}
                    onChange={(e) => setFfDisputeEmail(e.target.value)}
                    className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-[#f0f6ff] outline-none focus:border-white/25"
                    autoComplete="email"
                  />
                </label>
                <button
                  type="button"
                  disabled={ffDisputeBusy}
                  onClick={() => {
                    void (async () => {
                      const email = ffDisputeEmail.trim()
                      if (!email) {
                        setFfDisputeErr('Enter your buyer email.')
                        return
                      }
                      setFfDisputeBusy(true)
                      setFfDisputeErr('')
                      try {
                        await downloadBuyerDisputePdf({ kind: 'manual_ff', orderId: manualOrder.id, email })
                      } catch (e) {
                        setFfDisputeErr(e instanceof Error ? e.message : 'Download failed.')
                      } finally {
                        setFfDisputeBusy(false)
                      }
                    })()
                  }}
                  className="mt-3 w-full rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold text-[#dbe2ff] hover:bg-white/10 disabled:opacity-50"
                >
                  {ffDisputeBusy ? 'Preparing PDF…' : 'Download dispute pack (PDF)'}
                </button>
                {ffDisputeErr ? <p className="mt-2 text-xs text-red-300">{ffDisputeErr}</p> : null}
              </div>
            ) : null}
          </section>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-5">
      <div
        className="rounded-2xl p-8 sm:p-10 max-w-xl w-full"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(0,191,255,0.08)' }}
      >
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
          style={{ background: 'linear-gradient(135deg, rgba(74,222,128,0.2), rgba(74,222,128,0.05))', border: '1px solid rgba(74,222,128,0.3)' }}
        >
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#00bfff" strokeWidth="2.5">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
        <h1 className="font-display text-4xl tracking-wide mb-4 text-center" style={{ color: '#00bfff' }}>ORDER CONFIRMED</h1>
        <p className="text-sm leading-relaxed mb-4 text-center" style={{ color: '#8ecfff' }}>
          {buyerServerPoll.smm
            ? smmCheckoutSuccessMessage(buyerServerPoll.smm.smmPlatform, buyerServerPoll.smm.smmMetric)
            : fromCrypto
              ? 'Your cryptocurrency payment was received. Support will match it to your order and fulfill via Discord or email.'
              : 'Payment successful. Your order will be fulfilled by support after ticket verification.'}
        </p>
        {buyerServerPoll.smm ? (
          <div className="mb-5 text-left">
            <BuyerSmmFulfillment
              {...buyerServerPoll.smm}
              orderId={
                fromCrypto && cryptoPaymentId
                  ? cryptoPaymentId
                  : mmCardCtx?.checkoutSessionId
              }
              buyerEmail={cryptoLedger?.buyerEmail ?? mmCardCtx?.buyerEmail}
            />
            {fromCrypto && cryptoPaymentId && cryptoLedger?.buyerEmail ? (
              <p className="mt-3 text-center text-xs text-[#5a6d8a]">
                Bookmark{' '}
                <Link
                  to="/customer/orders/$orderId"
                  params={{ orderId: cryptoPaymentId }}
                  search={{ email: cryptoLedger.buyerEmail }}
                  className="font-mono text-[#a5d8ff] hover:underline"
                >
                  track this order
                </Link>{' '}
                to check status anytime.
              </p>
            ) : null}
          </div>
        ) : null}
        {mmCardCtx ? (
          <div
            className="mb-5 rounded-xl px-4 py-4 text-sm"
            style={{ background: 'rgba(88,101,242,0.12)', border: '1px solid rgba(88,101,242,0.28)' }}
          >
            <p className="font-semibold text-[#dbe2ff]">Card checkout reference</p>
            <p className="mt-1 text-xs leading-relaxed text-[#c9d4ff]">
              Session ID matches our payment webhook. The Discord template below also lists what you bought (from your
              cart).
            </p>
            {buyerServerPoll.kind === 'moneymotion' && buyerServerPoll.ready === true ? (
              <p
                className="mt-3 rounded-lg border border-emerald-500/35 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-100"
                role="status"
              >
                Our server has recorded your card payment for this session. You can open Discord whenever you are ready
                — support will see the same status in the admin dashboard.
              </p>
            ) : buyerServerPoll.kind === 'moneymotion' && buyerServerPoll.ready === false ? (
              <p className="mt-3 text-xs text-[#a8b3e8]" role="status">
                Waiting for the payment webhook (usually within a minute after your bank approves). This page checks
                automatically.
              </p>
            ) : null}
            {buyerServerPoll.kind === 'moneymotion' && buyerServerPoll.err ? (
              <p className="mt-2 text-xs text-amber-200">{buyerServerPoll.err}</p>
            ) : null}
            <div className="mt-3 rounded-lg border border-white/10 bg-black/20 p-3 font-mono text-xs text-[#e8ecff] break-all">
              {mmCardCtx.checkoutSessionId}
            </div>
            {mmCardCtx.purchaseSummary ? (
              <div className="mt-3 rounded-lg border border-white/10 bg-black/20 p-3 text-xs leading-relaxed text-[#e8ecff]">
                <p className="mb-1 font-semibold text-[#dbe2ff]">Your cart</p>
                <ul className="m-0 list-disc space-y-0.5 pl-4 text-[#c9d4ff]">
                  {mmCardCtx.purchaseSummary.split('\n').map((line, idx) => (
                    <li key={idx} className="break-words">
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void copyText(mmCardCtx.checkoutSessionId, 'mmSession')}
                className="rounded-md border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#dbe2ff] hover:bg-white/10"
              >
                {copied === 'mmSession' ? 'Copied session ID' : 'Copy session ID'}
              </button>
            </div>
            <p className="mt-4 font-semibold text-[#dbe2ff]">Discord message template</p>
            <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-white/10 bg-black/20 p-3 font-mono text-xs leading-relaxed text-[#e8ecff]">
              {mmDiscordPaste}
            </pre>
            <button
              type="button"
              onClick={() => void copyText(mmDiscordPaste, 'mmPaste')}
              className="mt-2 rounded-md border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#dbe2ff] hover:bg-white/10"
            >
              {copied === 'mmPaste' ? 'Copied template' : 'Copy template'}
            </button>
            <div className="mt-4 border-t border-white/10 pt-4">
              <p className="font-semibold text-[#dbe2ff]">Card dispute pack (PDF)</p>
              <p className="mt-1 text-xs leading-relaxed text-[#c9d4ff]">
                Order summary for your bank or card network. If you just paid, wait a minute for capture, then try again.
              </p>
              <label className="mt-3 block">
                <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.12em] text-[#5a6d8a]">Checkout email</span>
                <input
                  type="email"
                  value={mmDisputeEmail}
                  onChange={(e) => setMmDisputeEmail(e.target.value)}
                  placeholder="Same email you used on the cart (optional if not stored)"
                  className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-[#f0f6ff] outline-none focus:border-white/25"
                  autoComplete="email"
                />
              </label>
              <p className="mt-1 text-[0.65rem] leading-snug text-[#5a6d8a]">
                If the order has no stored checkout email yet, leave this blank — your session ID is enough.
              </p>
              <button
                type="button"
                disabled={mmDisputeBusy}
                onClick={() => {
                  void (async () => {
                    if (!mmCardCtx) return
                    setMmDisputeBusy(true)
                    setMmDisputeErr('')
                    try {
                      await downloadBuyerDisputePdf({
                        kind: 'moneymotion',
                        checkoutSessionId: mmCardCtx.checkoutSessionId,
                        email: mmDisputeEmail.trim(),
                      })
                    } catch (e) {
                      setMmDisputeErr(e instanceof Error ? e.message : 'Download failed.')
                    } finally {
                      setMmDisputeBusy(false)
                    }
                  })()
                }}
                className="mt-3 w-full rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold text-[#dbe2ff] hover:bg-white/10 disabled:opacity-50"
              >
                {mmDisputeBusy ? 'Preparing PDF…' : 'Download dispute pack (PDF)'}
              </button>
              {mmDisputeErr ? <p className="mt-2 text-xs text-red-300">{mmDisputeErr}</p> : null}
            </div>
          </div>
        ) : fromCrypto && cryptoPaymentId ? (
          <div
            className="mb-5 rounded-xl px-4 py-4 text-sm"
            style={{ background: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.28)' }}
          >
            <p className="font-semibold text-[#e9d5ff]">Cryptocurrency checkout reference</p>
            <p className="mt-1 text-xs leading-relaxed text-[#d8b4fe]">
              This invoice ID matches our ledger. The template below includes your email and cart line items when
              available.
            </p>
            {buyerServerPoll.kind === 'crypto' && buyerServerPoll.ready === true ? (
              <p
                className="mt-3 rounded-lg border border-emerald-500/35 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-100"
                role="status"
              >
                Our server shows this invoice as finished on the crypto ledger. You can open Discord whenever you are
                ready — support matches this payment ID to your order.
              </p>
            ) : buyerServerPoll.kind === 'crypto' && buyerServerPoll.ready === false ? (
              <p className="mt-3 text-xs text-[#d8b4fe]" role="status">
                Waiting for the blockchain confirmation status to reach &quot;finished&quot;. This page checks automatically.
              </p>
            ) : null}
            {buyerServerPoll.kind === 'crypto' && buyerServerPoll.err ? (
              <p className="mt-2 text-xs text-amber-200">{buyerServerPoll.err}</p>
            ) : null}
            <div className="mt-3 rounded-lg border border-white/10 bg-black/20 p-3 font-mono text-xs text-[#f3e8ff] break-all">
              {cryptoPaymentId}
            </div>
            {cryptoLedgerLoading ? (
              <p className="mt-3 text-xs text-[#a78bfa]">Loading order line items…</p>
            ) : null}
            {!cryptoLedgerLoading && cryptoLedger?.items?.length ? (
              <div className="mt-3 rounded-lg border border-white/10 bg-black/20 p-3 text-xs leading-relaxed text-[#f3e8ff]">
                <p className="mb-1 font-semibold text-[#e9d5ff]">Your cart</p>
                <ul className="m-0 list-disc space-y-0.5 pl-4 text-[#d8b4fe]">
                  {orderItemsToPasteSummary(cryptoLedger.items)
                    .split('\n')
                    .map((line, idx) => (
                      <li key={idx} className="break-words">
                        {line}
                      </li>
                    ))}
                </ul>
              </div>
            ) : null}
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void copyText(cryptoPaymentId, 'cryptoPayId')}
                className="rounded-md border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#e9d5ff] hover:bg-white/10"
              >
                {copied === 'cryptoPayId' ? 'Copied payment ID' : 'Copy payment ID'}
              </button>
            </div>
            <p className="mt-4 font-semibold text-[#e9d5ff]">Discord message template</p>
            <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-white/10 bg-black/20 p-3 font-mono text-xs leading-relaxed text-[#f3e8ff]">
              {cryptoDiscordPaste}
            </pre>
            <button
              type="button"
              onClick={() => void copyText(cryptoDiscordPaste, 'cryptoPaste')}
              className="mt-2 rounded-md border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#e9d5ff] hover:bg-white/10"
            >
              {copied === 'cryptoPaste' ? 'Copied template' : 'Copy template'}
            </button>
          </div>
        ) : fromCrypto ? (
          <div
            className="mb-5 rounded-xl px-4 py-3 text-xs leading-relaxed"
            style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.22)', color: '#fde68a' }}
          >
            Open the crypto checkout page from your history and tap <span className="font-semibold">Order complete</span>{' '}
            so we can attach your payment ID and cart to this message automatically.
          </div>
        ) : null}
        <div
          className="rounded-xl px-4 py-3 mb-5 text-xs"
          style={{ background: 'rgba(0, 191, 255, 0.08)', border: '1px solid rgba(0,191,255,0.18)', color: '#ffd2ee' }}
        >
          {fromCrypto
            ? 'If you have not already, open a Discord ticket and paste the template (or your payment ID and email).'
            : 'Delivery starts after you open a support ticket in Discord.'}
        </div>
        <div className="space-y-2 mb-8 text-sm">
          <p style={{ color: '#f0f6ff' }}><span className="font-semibold">1.</span> Open a Discord ticket using the button below.</p>
          <p style={{ color: '#f0f6ff' }}>
            <span className="font-semibold">2.</span>{' '}
            {mmCardCtx || (fromCrypto && cryptoDiscordPaste)
              ? 'Paste the template above (IDs, email, and what you bought) into the ticket.'
              : fromCrypto
                ? 'Include your crypto payment ID and the email you used at checkout so support can find your order.'
                : 'Paste your order confirmation email in the ticket.'}
          </p>
          <p style={{ color: '#f0f6ff' }}><span className="font-semibold">3.</span> Staff verifies and sends your product details.</p>
          <p style={{ color: '#f0f6ff' }}>
            <span className="font-semibold">4.</span> Test your order. If it works, post in Discord{' '}
            <a href={PulseDigital_VOUCHES_CHANNEL_URL} target="_blank" rel="noopener noreferrer" className="font-semibold underline decoration-[#00bfff]/40 underline-offset-2 hover:text-[#a5d8ff]">
              {PulseDigital_VOUCHES_CHANNEL}
            </a>{' '}
            within 24 hours (order ID + product) to activate warranty.
          </p>
          <p style={{ color: '#f0f6ff' }}><span className="font-semibold">5.</span> If it never worked on first use, reply in your ticket before vouching — do not vouch a broken delivery.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/shop"
            className="btn-primary px-6 py-3 rounded-xl text-sm font-semibold"
          >
            Continue Shopping
          </Link>
          <Link
            to="/customer/orders"
            search={{
              orderId:
                mmCardCtx?.checkoutSessionId?.trim() ||
                (fromCrypto && cryptoPaymentId ? cryptoPaymentId : '') ||
                (manualOrderId?.trim() ? manualOrderId.trim() : ''),
              email:
                (mmCardCtx?.buyerEmail?.trim() || mmDisputeEmail.trim()) ||
                (fromCrypto ? cryptoLedger?.buyerEmail?.trim() || '' : '') ||
                (manualOrder?.buyerEmail?.trim() || ffDisputeEmail.trim()) ||
                '',
            }}
            className="btn-chrome px-6 py-3 rounded-xl text-sm inline-flex items-center justify-center gap-2"
          >
            Track in Dashboard
          </Link>
          <a
            href="https://discord.gg/tGxhDfRkdE"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-discord rounded-xl px-6 py-3 text-sm"
          >
            <DiscordIcon size={16} white className="shrink-0" />
            Open Discord Ticket
          </a>
        </div>
      </div>
    </div>
  )
}
